import { type ReactNode } from 'react';
import { SignOutIcon } from '@phosphor-icons/react';

import { useAuth } from '@/contexts/auth-context';
import { ThemeToggle } from './ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';

function UserAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="size-7 shrink-0 rounded-full object-cover" />;
  }
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-tint">
      <span className="text-2xs font-semibold leading-none text-brand-text">{name.charAt(0).toUpperCase()}</span>
    </div>
  );
}

interface SidebarUserMenuProps {
  /** Extra menu items rendered above the Log out action (e.g. a Settings link). */
  menuItems?: ReactNode;
}

/** Shared sidebar footer row: the current-user dropdown (with Log out) + theme toggle. */
export function SidebarUserMenu({ menuItems }: SidebarUserMenuProps) {
  const { me, logout } = useAuth();

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="User menu"
            className="flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg px-1.5 text-left transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <UserAvatar name={me.name} avatarUrl={me.avatarUrl} />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium leading-tight text-fg">{me.name}</span>
              <span className="truncate text-2xs leading-tight text-fg-tertiary">{me.email}</span>
            </div>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" side="top" className="w-56">
          {menuItems && (
            <>
              <DropdownMenuGroup>{menuItems}</DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuGroup>
            <DropdownMenuItem destructive onSelect={logout}>
              <SignOutIcon size={15} />
              Log out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <ThemeToggle />
    </div>
  );
}
